import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getPortalToken, clearPortalData, setSurface } from '../utils/portalStorage';

/**
 * Axios instance for the CUSTOMER PORTAL.
 *
 * Base path is `/api/v1/portal` — every portal endpoint hangs off it, so callers
 * pass relative paths like `/orders`, `/auth/login`, `/me`.
 *
 * It attaches the PORTAL bearer token (never the staff `auth_token`). On 401 it
 * clears the portal session and drops the app back to the portal login surface
 * (not the staff `/login`). 429 is passed through for the caller to surface as a
 * "slow down" toast.
 */
const portalApiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/v1/portal`,
  headers: { 'Content-Type': 'application/json' },
});

portalApiClient.interceptors.request.use(
  (config) => {
    const token = getPortalToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

portalApiClient.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;
    const url = err.config?.url || '';
    // 401 on the login endpoint is a bad-credentials response the login screen
    // handles inline — don't wipe the session or bounce the surface for it.
    const isLogin = url.includes('/auth/login');
    if (status === 401 && !isLogin) {
      clearPortalData();
      setSurface('portal'); // stay on the portal surface, but PortalAuthProvider will show login
      window.dispatchEvent(new CustomEvent('portal-session-expired'));
    }
    // 403 on a transactional endpoint means the customer's access changed
    // server-side (e.g. approval revoked / de-approved) while a stale tab still
    // shows the full portal. Ask the shell to re-fetch /me and re-gate, rather
    // than leaving the customer stuck on a screen they can no longer use. Skip
    // the onboarding endpoints themselves (their 403s are handled inline).
    const isOnboardingCall = /\/me(\/|$)/.test(url) || isLogin;
    if (status === 403 && !isOnboardingCall) {
      window.dispatchEvent(new CustomEvent('portal-access-changed'));
    }
    return Promise.reject(err);
  }
);

export default portalApiClient;
